'use client';

import { useEffect } from 'react';
import { platform } from '@/lib/native/is-native';

/**
 * Initialises Amplitude once, client-side, for EVERY surface — including the
 * logged-out marketing/login/signup pages. That's deliberate: we want the
 * signup → onboarding activation funnel, which starts before auth. The user
 * is anonymous (device id only) until AmplitudeIdentify runs post-login.
 *
 * Mounted in the root layout next to <Analytics />. Renders nothing.
 */
export default function AmplitudeProvider() {
  useEffect(() => {
    // Dynamic import keeps the Amplitude SDK + session-replay + rrweb
    // (~100-150KB gzip) out of every route's first-load JS — it only ever
    // runs here, in an effect, so there's no reason to ship it eagerly on
    // /login and every other page.
    let cancelled = false;
    void import('@/lib/analytics/amplitude').then(({ initAmplitude }) => {
      if (!cancelled) initAmplitude({ platform: resolvePlatform() });
    });
    // AppsFlyer install attribution — native shell only, no-op on web/PWA.
    // Also raises the iOS ATT prompt on first launch.
    void import('@/lib/native/appsflyer').then(({ initAppsFlyer }) => {
      if (!cancelled) void initAppsFlyer();
    });
    return () => { cancelled = true; };
  }, []);
  return null;
}

/**
 * web | pwa | ios | android. Capacitor tells us native; for the browser we
 * distinguish an installed PWA (standalone display-mode) from a normal tab
 * so we can compare engagement across install surfaces.
 */
function resolvePlatform(): string {
  const p = platform(); // 'ios' | 'android' | 'web'
  if (p !== 'web') return p;
  if (
    typeof window !== 'undefined' &&
    window.matchMedia?.('(display-mode: standalone)').matches
  ) {
    return 'pwa';
  }
  return 'web';
}
